import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { UseFormReturn } from "react-hook-form";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ControlRegime, MedicalPrescriptionInput } from "@/lib/validations/medical-prescription";
import { cn } from "@/lib/utils";

/** "Dados da receita" collapsible: validity (always) plus any extra fields passed as children. */
export function PrescriptionDetailsCollapsible({
  validity,
  onValidityChange,
  error,
  mobile = false,
  children,
}: {
  validity: number | undefined;
  onValidityChange: (value: number | undefined) => void;
  error?: string;
  mobile?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation("prescriptions");
  const [open, setOpen] = useState(false);
  return (
    <section className={cn("border border-border/70 px-3.5 py-3", mobile ? "rounded-2xl p-3.5" : "rounded-[14px]")}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-left text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex-1">
          {t("editor.meta.title")}
          <span className="font-normal text-muted-foreground"> · {t("editor.meta.validitySummary", { days: validity ?? "—" })}</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="meta-validity" className="text-sm font-medium">{t("examComposer.validityDays")}</Label>
            <Input
              id="meta-validity"
              type="number"
              min={1}
              max={365}
              className={mobile ? "h-12 text-base" : undefined}
              value={validity ?? ""}
              onChange={(e) => onValidityChange(e.target.value.trim() === "" ? undefined : Number(e.target.value))}
              aria-invalid={Boolean(error)}
            />
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>
          {children}
        </div>
      ) : null}
    </section>
  );
}

export function PrescriptionMetaFields({
  form,
  kind,
  suggestedRegime,
  mobile = false,
}: {
  form: UseFormReturn<MedicalPrescriptionInput>;
  kind: "standard" | "controlled" | "manipulated";
  suggestedRegime: ControlRegime | null;
  mobile?: boolean;
}) {
  const { t } = useTranslation("prescriptions");
  const regime = form.watch("control_regime");
  const isLegacyControlled = form.watch("type") === "controlled";
  return (
    <PrescriptionDetailsCollapsible
      mobile={mobile}
      validity={form.watch("validity_in_days")}
      error={form.formState.errors.validity_in_days?.message}
      onValidityChange={(value) => form.setValue("validity_in_days", value, { shouldValidate: true })}
    >
      {kind === "standard" ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">{t("composer.controlRegime.label")}</Label>
            {suggestedRegime && suggestedRegime !== "none" ? (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-xs text-amber-600">{t("composer.controlRegime.suggestion")}</Badge>
            ) : null}
          </div>
          <Select
            value={regime}
            onValueChange={(value) => {
              form.setValue("control_regime", value as ControlRegime, { shouldValidate: true });
              form.setValue("validity_in_days", value === "antimicrobial" ? 10 : 30, { shouldValidate: true });
            }}
          >
            <SelectTrigger><SelectValue placeholder={t("composer.controlRegime.label")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("composer.controlRegime.none")}</SelectItem>
              <SelectItem value="antimicrobial">{t("composer.controlRegime.antimicrobial")}</SelectItem>
            </SelectContent>
          </Select>
          {regime === "antimicrobial" ? <p className="text-xs text-muted-foreground">{t("composer.controlRegime.antimicrobialHint")}</p> : null}
        </div>
      ) : null}
      {isLegacyControlled ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="meta-program" className="text-sm font-medium">{t("composer.regulatoryAgency")}</Label>
            <Input id="meta-program" placeholder={t("composer.regulatoryAgencyPlaceholder")} {...form.register("control_program")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta-identifier" className="text-sm font-medium">{t("composer.identifier")}</Label>
            <Input id="meta-identifier" placeholder={t("composer.identifierPlaceholder")} {...form.register("control_identifier")} />
          </div>
        </>
      ) : null}
    </PrescriptionDetailsCollapsible>
  );
}
